import { useEffect, useState } from "react";
import { API_URL } from "@/config";
import axios from "axios";
import { Users, Phone, Calendar } from "lucide-react";

const http = axios.create({ baseURL: API_URL, withCredentials: true });

interface Dealer { id: number; company: string; city: string | null; state: string | null; }
interface Lead {
    cust_leadid: number;
    cust_fname: string;
    cust_lname: string;
    cust_email: string;
    cust_phoneno: string | null;
    cust_lead_type: string | null;
    cust_status: string | null;
    cust_dealer_id: number;
    cust_lead_created_at: string | null;
}
interface Appointment {
    dlr_appt_id: number;
    dlr_appt_did: number;
    dlr_appt_title: string;
    dlr_appt_notes: string | null;
    dlr_appt_starttime_humanread: string | null;
    dlr_appt_salesname_txt: string | null;
    dlr_appt_created_at: string;
}

export const BdcDashboardPage = () => {
    const [dealers, setDealers] = useState<Dealer[]>([]);
    const [leads, setLeads] = useState<Lead[]>([]);
    const [appointments, setAppointments] = useState<Appointment[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeDealer, setActiveDealer] = useState<number | "ALL">("ALL");
    const [tab, setTab] = useState<"leads" | "appointments">("leads");

    useEffect(() => {
        Promise.all([
            http.get<Dealer[]>("/bdc/dealers").then(r => r.data).catch(() => []),
            http.get<Lead[]>("/bdc/leads").then(r => r.data).catch(() => []),
            http.get<Appointment[]>("/bdc/appointments").then(r => r.data).catch(() => []),
        ]).then(([d, l, a]) => {
            setDealers(d);
            setLeads(l);
            setAppointments(a);
        }).finally(() => setLoading(false));
    }, []);

    const visibleLeads = activeDealer === "ALL" ? leads : leads.filter(l => l.cust_dealer_id === activeDealer);
    const visibleAppts = activeDealer === "ALL" ? appointments : appointments.filter(a => a.dlr_appt_did === activeDealer);

    const todayAppts = appointments.filter(a => {
        const d = new Date(a.dlr_appt_created_at);
        const today = new Date();
        return d.toDateString() === today.toDateString();
    }).length;

    return (
        <div className="p-6 space-y-6">
            <h1 className="text-2xl font-light uppercase tracking-wide">BDC Dashboard</h1>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
                {[
                    { label: "Total Leads", value: leads.length, icon: Users, color: "text-indigo-600 bg-indigo-50" },
                    { label: "Dealers", value: dealers.length, icon: Phone, color: "text-blue-600 bg-blue-50" },
                    { label: "Appts Today", value: todayAppts, icon: Calendar, color: "text-green-600 bg-green-50" },
                ].map(s => (
                    <div key={s.label} className="bg-white rounded-2xl shadow p-5 flex items-center gap-4">
                        <div className={`p-3 rounded-xl ${s.color}`}>
                            <s.icon className="h-5 w-5" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold">{loading ? "—" : s.value}</p>
                            <p className="text-xs text-gray-500 uppercase">{s.label}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Dealer Tabs */}
            <div className="flex gap-2 overflow-x-auto pb-1">
                <button
                    onClick={() => setActiveDealer("ALL")}
                    className={`px-4 py-1.5 text-sm rounded-full border whitespace-nowrap transition ${activeDealer === "ALL" ? "bg-indigo-600 text-white border-indigo-600" : "border-gray-200 text-gray-600 hover:border-indigo-400"}`}
                >
                    All Dealers
                </button>
                {dealers.map(d => (
                    <button
                        key={d.id}
                        onClick={() => setActiveDealer(d.id)}
                        className={`px-4 py-1.5 text-sm rounded-full border whitespace-nowrap transition ${activeDealer === d.id ? "bg-indigo-600 text-white border-indigo-600" : "border-gray-200 text-gray-600 hover:border-indigo-400"}`}
                    >
                        {d.company}
                    </button>
                ))}
            </div>

            {/* Content Tabs */}
            <div className="flex gap-4 border-b">
                {(["leads", "appointments"] as const).map(t => (
                    <button
                        key={t}
                        onClick={() => setTab(t)}
                        className={`pb-2 text-sm font-medium capitalize border-b-2 transition ${tab === t ? "border-indigo-600 text-indigo-600" : "border-transparent text-gray-500"}`}
                    >
                        {t} {t === "leads" ? `(${visibleLeads.length})` : `(${visibleAppts.length})`}
                    </button>
                ))}
            </div>

            {tab === "leads" && (
                <div className="bg-white rounded-2xl shadow p-6">
                    {loading ? (
                        <p className="text-sm text-gray-400">Loading leads...</p>
                    ) : visibleLeads.length === 0 ? (
                        <p className="text-sm text-gray-400">No leads found.</p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="text-xs uppercase text-gray-400 border-b">
                                <tr>
                                    <th className="text-left py-2">Name</th>
                                    <th className="text-left py-2">Email</th>
                                    <th className="text-left py-2">Phone</th>
                                    <th className="text-left py-2">Type</th>
                                    <th className="text-left py-2">Status</th>
                                    <th className="text-left py-2">Date</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {visibleLeads.map(l => (
                                    <tr key={l.cust_leadid} className="hover:bg-gray-50">
                                        <td className="py-2 font-medium">{l.cust_fname} {l.cust_lname}</td>
                                        <td className="py-2 text-gray-500">{l.cust_email}</td>
                                        <td className="py-2">{l.cust_phoneno ?? "—"}</td>
                                        <td className="py-2">{l.cust_lead_type ?? "—"}</td>
                                        <td className="py-2">
                                            <span className="px-2 py-0.5 rounded-full text-xs bg-indigo-100 text-indigo-700">
                                                {l.cust_status ?? "NEW"}
                                            </span>
                                        </td>
                                        <td className="py-2 text-gray-400">
                                            {l.cust_lead_created_at ? new Date(l.cust_lead_created_at).toLocaleDateString() : "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {tab === "appointments" && (
                <div className="bg-white rounded-2xl shadow p-6">
                    {loading ? (
                        <p className="text-sm text-gray-400">Loading appointments...</p>
                    ) : visibleAppts.length === 0 ? (
                        <p className="text-sm text-gray-400">No appointments found.</p>
                    ) : (
                        <div className="space-y-3">
                            {visibleAppts.map(a => (
                                <div key={a.dlr_appt_id} className="border rounded-xl px-4 py-3">
                                    <div className="flex items-center justify-between">
                                        <p className="font-medium">{a.dlr_appt_title}</p>
                                        <span className="text-xs text-gray-400">{a.dlr_appt_starttime_humanread ?? "—"}</span>
                                    </div>
                                    {a.dlr_appt_notes && <p className="text-xs text-gray-400 mt-1">{a.dlr_appt_notes}</p>}
                                    {a.dlr_appt_salesname_txt && (
                                        <p className="text-xs text-gray-500 mt-1">SP: {a.dlr_appt_salesname_txt}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default BdcDashboardPage;
